import { adminApiAxios } from '@/api'
import {
  PrismaCursorPagination,
  CursorPaginatedResponse,
} from '../../../utils/fetchHelpers'
import { Transaction } from '../../../reducers/admin/allTransactions.slice'
import { OrderBy } from '../../../v2-features/admin/paginated-table/AdminBaseTable'
import { serializeOrderBy } from '../../../utils/sortHelpers'
import { fetchWrapper } from '@/reducers/fetch'

const V2_TRANSACTIONS_API_BASE = '/finances/api/v2/admin/transactions'

export type TransactionFilter = {
  userId?: number
  financialAccountId?: number
  transactionsType?: string
  transactionCategoryId?: number
  startDate?: string
  endDate?: string
  lastReviewDate?: string
  currentTaxYear?: string
  includePersonal?: boolean
  includeSplitChildren?: boolean
  notesLastUpdatedAtStart?: string
  notesLastUpdatedAtEnd?: string
  descriptionSearch?: string
  notesSearch?: string
}

export const fetchPaginatedTransactions = async ({
  filter,
  pagination,
  orderBy,
  select = [],
}: {
  filter?: TransactionFilter
  pagination?: PrismaCursorPagination
  orderBy?: OrderBy
  select?: string[]
}) => {
  const res = await adminApiAxios.get<CursorPaginatedResponse<Transaction>>(
    V2_TRANSACTIONS_API_BASE,
    {
      params: {
        ...(filter ? filter : {}),
        take: 10,
        ...(pagination ? pagination : {}),
        orderBy: serializeOrderBy(orderBy),
        selectRelation: select,
      },
      paramsSerializer: {
        indexes: false,
      },
    }
  )
  return res.data
}

export const FETCH_ALL_ADMIN_TRANSACTION_PAGINATED_KEY =
  'FETCH_ALL_ADMIN_TRANSACTION_PAGINATED_KEY'

/**
 * Fetches all transactions that match the filter.
 *
 * WARNING: This has the potential to be a large operation and should
 * be used with caution, with sufficient filtering constraints. Please
 * do not use this to fetch all transactions for a user, or worse.
 *
 * @param filter - The filter to apply to the transactions
 * @param orderBy - The order by to apply to the transactions
 * @param select - The select to apply to the transactions
 * @returns The transactions that match the filter
 */
export const fetchAllAdminTransactionPaginated = ({
  filter,
  orderBy,
  select = [],
}: {
  filter?: TransactionFilter
  orderBy?: OrderBy
  select?: string[]
}) =>
  fetchWrapper({
    fetchKey: FETCH_ALL_ADMIN_TRANSACTION_PAGINATED_KEY,
    defaultErrorMessage: 'Error fetching all transactions',
    defaultValue: [] as Transaction[],
    fetchFunction: async () => {
      const transactions: Transaction[] = []
      let pagination: PrismaCursorPagination = { take: 500 }
      do {
        const result = await fetchPaginatedTransactions({
          filter,
          pagination,
          orderBy,
          select,
        })
        if (result.data) {
          transactions.push(...result.data)
        }
        pagination = { ...pagination, cursor: result.pagination?.nextCursor }
      } while (pagination.cursor)
      return transactions
    },
  })

export const countTransactions = async (filter?: TransactionFilter) => {
  const res = await adminApiAxios.get<{ count: number }>(
    `${V2_TRANSACTIONS_API_BASE}/count`,
    {
      params: {
        ...(filter ? filter : {}),
      },
      paramsSerializer: {
        indexes: false,
      },
    }
  )
  return res.data.count
}

export const fetchTransaction = async ({
  id,
  select,
}: {
  id: number
  select?: string[]
}) => {
  const res = await adminApiAxios.get<Transaction>(
    `${V2_TRANSACTIONS_API_BASE}/${id}`,
    {
      params: {
        selectRelation: select,
      },
      paramsSerializer: {
        indexes: false,
      },
    }
  )
  return res.data
}
